import React from 'react';
import Button from '@common/button';
import Text from '@common/text';
import Checkbox from '@common/checkbox';
import EmptyContents from '@components/profile/modals/nhisCareerModal/step5/emptyContents';
import { useNHISCompaniesReport } from '@repository/nhis/useNHIS';
import NhisCompanyReportHistoryDto from '@domain/rs/nhis/NhisCompanyReportHistoryDto';
import { FontStyle } from '@domain/constant/fontStyle';
import NumberFormat from '@utils/number/format';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ListItemProps {
  isSelect: boolean;
  companyName?: string;
  date?: string;
  salary?: string;
  handleChange: (checked: boolean) => void;
}

const ListItem = ({ isSelect, companyName, date, salary, handleChange }: ListItemProps) => {
  if (!companyName && !date && !salary) return null;

  return (
    <label className={cx('itemArea', { select: isSelect })}>
      <div className={cx('contentsArea')}>
        {companyName && <Text fontStyle={FontStyle.B1_B}>{companyName}</Text>}
        <div className={cx('contentsWrap')}>
          {date && <Text fontStyle={FontStyle.B3_M}>{date}</Text>}
          {salary && (
            <Text className={cx('salary')} fontStyle={FontStyle.B3_M}>
              {salary}
            </Text>
          )}
        </div>
      </div>
      <Checkbox
        className={cx('checkbox')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.checked)}
        checked={isSelect}
      />
    </label>
  );
};

interface Step5Props {
  nextStep: () => void;
  onClose: () => void;
  selectedCompanies: NhisCompanyReportHistoryDto[];
  setSelectedCompanies: React.Dispatch<React.SetStateAction<NhisCompanyReportHistoryDto[]>>;
}

const Step5 = ({ nextStep, onClose, selectedCompanies, setSelectedCompanies }: Step5Props) => {
  const { data: companiesData } = useNHISCompaniesReport();
  const isDisabled = selectedCompanies.length === 0;

  const els = companiesData?.companies?.map((company) => {
    const isSelect = selectedCompanies.some((item) => item.token === company.token);
    const endDateText = company?.endDate ? `${company?.endDate}` : '재직중';
    const dateText = `${company?.startDate} ~ ${endDateText}`;
    const salaryText = `${NumberFormat.format(company.totalSalary, '10K', ' ', ['', '만', '억'], (value) =>
      NumberFormat.formatDecimal(value),
    )}원`;

    const handleChange = (checked: boolean) => {
      setSelectedCompanies((prev) =>
        checked ? [...prev, company] : prev.filter((item) => item.token !== company.token),
      );
    };

    return (
      <ListItem
        key={company.token}
        isSelect={isSelect}
        companyName={company.companyName}
        date={dateText}
        salary={salaryText}
        handleChange={handleChange}
      />
    );
  });

  if (!companiesData || companiesData?.companies?.length === 0) return <EmptyContents onClose={onClose} />;

  return (
    <div className={cx('area')}>
      <div className={cx('wrap')}>
        <em className={cx('desc')}>
          성공적으로 경력을 불러왔어요!
          <br />
          등록할 경력 정보를 선택해주세요.
        </em>

        <div className={cx('listArea')}>
          <div className={cx('listWrap')}>{els}</div>
        </div>
      </div>

      <div className={cx('btnArea')}>
        <Button className={cx('btn')} type={'button'} onClick={nextStep} disabled={isDisabled}>
          계속
        </Button>
      </div>
    </div>
  );
};

export default Step5;
